import React from 'react';
import {connect} from 'react-redux';
import _ from 'underscore';

import {assert} from 'Asserts';
import {BenchlingState} from 'redux-stores/reduceApp';
import {BenchlingDispatch} from 'redux-stores/Store';
import {
  bulkUpdateFulfillments,
  fetchRequestFulfillments,
} from 'requests/fulfillments/FulfillmentReduxActions';
import {RequestSchema} from 'requests/RequestConstants';
import * as RequestTaskSchemaReduxActions from 'requests/schemas/RequestTaskSchemaReduxActions';
import * as AssaySchemaActions from 'results/AssaySchemaActions';
import AssayStore from 'results/AssayStore';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {ButtonStyle} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Modal from 'ui-library/modals/Modal';
import manageRowState, {ManageRowStateProps} from 'util/manageRowState';
import {withLoadingScreen} from 'util/Promises';
import {Workflow} from 'workflows/WorkflowConstants';

import {
  FULFILLMENT_STATUS_NAMES,
  FulfillmentStatus,
  RequestFulfillment,
  RequestFulfillmentUpdate,
} from './FulfillmentConstants';
import FulfillmentStatusTable from './FulfillmentStatusTable';
import {HydratedFulfillment} from './HydratedFulfillmentConstants';
import hydrateFulfillmentInputs, {MissingDataError} from './hydrateFulfillmentInputs';

function getFulfillments(
  state: BenchlingState,
  workflow: Workflow
): {fulfillmentIds: Array<string>; fulfillments: Array<RequestFulfillment> | null} {
  const {fulfillmentsById: allFulfillmentsById} = state.models.requests.fulfillments;
  const fulfillmentIds = _.compact(
    workflow.initializationDatas.map((initData) => initData.requestFulfillmentId)
  );
  assert(
    fulfillmentIds.length > 0,
    'Should not open fulfillment status modal when there are no fulfillments'
  );

  const fulfillments = fulfillmentIds.every((id) => allFulfillmentsById[id] != null)
    ? fulfillmentIds.map((id) => allFulfillmentsById[id])
    : null;

  return {fulfillmentIds, fulfillments};
}

function mapStateToProps(
  state: BenchlingState,
  {requestSchemas, workflow}: BaseUpdateFulfillmentStatusModalProps & AssayStoreProps
): ConnectedProps {
  const {sampleGroupsById} = state.models.sampleGroups;
  const {requestsById, requestTasksById} = state.models.requests.inputs;
  const {requestTaskSchemasById} = state.models.requests.schemas;

  const {fulfillmentIds, fulfillments} = getFulfillments(state, workflow);
  const loadingProps = {
    fulfillmentIds,
    hydratedFulfillments: null,
  };

  if (fulfillments == null) {
    return loadingProps;
  }

  try {
    return {
      fulfillmentIds,
      hydratedFulfillments: hydrateFulfillmentInputs(
        fulfillments,
        requestsById,
        sampleGroupsById,
        requestTasksById,
        _.indexBy(requestSchemas, 'id'),
        requestTaskSchemasById!
      ),
    };
  } catch (e) {
    if (e instanceof MissingDataError) {
      return loadingProps;
    } else {
      throw e;
    }
  }
}

interface BaseUpdateFulfillmentStatusModalProps {
  onRequestClose: () => void;
  workflow: Workflow;
}

interface AssayStoreProps {
  requestSchemas: Array<RequestSchema>;
}

interface ConnectedProps {
  fulfillmentIds: Array<string>;
  hydratedFulfillments: Array<HydratedFulfillment> | null;
}

interface DispatchProps {
  bulkUpdateFulfillments: (
    updates: Array<{fulfillmentId: string; status: FulfillmentStatus}>
  ) => Promise<void>;
  fetchRequestFulfillments: (fulfillmentIds: Array<string>) => void;
  fetchRequestTaskSchemas: () => void;
}

interface RowState {
  id: string;
  selected: boolean;
  newStatus: FulfillmentStatus;
}

interface State {
  isSubmitting: boolean;
}

interface RowStateManagerProps extends ManageRowStateProps<RowState> {
  setNewStatusForSelectedRows: (newStatus: FulfillmentStatus) => void;
  toggleRowSelected: (rowId: string) => void;
  toggleAllRowsSelected: () => void;
}

type UpdateFulfillmentStatusModalProps = BaseUpdateFulfillmentStatusModalProps &
  AssayStoreProps &
  ConnectedProps &
  DispatchProps &
  RowStateManagerProps;

class UpdateFulfillmentStatusModal extends React.Component<UpdateFulfillmentStatusModalProps> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any

  override state: State = {
    isSubmitting: false,
  };

  override componentDidMount(): void {
    AssaySchemaActions.fetchAllAssaySchemas();
    this.props.fetchRequestTaskSchemas();
    this.props.fetchRequestFulfillments(this.props.fulfillmentIds);
  }

  _getStatusMenu(): JSX.Element {
    return (
      <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.MEDIUM}>
        {Object.values(FulfillmentStatus).map(
          (status) =>
            // Cannot set the status to PENDING. PENDING is a dynamically calculated status - it is never
            // persisted on the server.
            status !== FulfillmentStatus.PENDING && (
              <Option key={status} value={status} onSelect={this.props.setNewStatusForSelectedRows}>
                {FULFILLMENT_STATUS_NAMES[status]}
              </Option>
            )
        )}
      </Menu>
    );
  }

  _handleSubmit = async (): Promise<void> => {
    try {
      this.setState({isSubmitting: true});
      await withLoadingScreen(
        this.props.bulkUpdateFulfillments(
          this.props.rowStates
            .filter((rowState) => rowState.newStatus != null)
            .map((rowState) => ({fulfillmentId: rowState.id, status: rowState.newStatus}))
        ),
        'Updating requests'
      );
      this.props.onRequestClose();
    } finally {
      this.setState({isSubmitting: false});
    }
  };

  override render(): JSX.Element {
    const isLoading = !this.props.hydratedFulfillments;

    return (
      <Modal
        large={true}
        title="Update request status"
        onRequestClose={this.props.onRequestClose}
        buttons={[
          {
            text: this.state.isSubmitting ? 'Updating...' : 'Update',
            style: ButtonStyle.SECONDARY,
            disabled:
              this.state.isSubmitting || this.props.rowStates.every((rowState) => rowState.newStatus == null),
            onClick: this._handleSubmit,
          },
        ]}
      >
        <Row className="u-vertical-center-items u-small-spacing">
          <Col md={4} />
          <Col md={4} className="u-text-align-right">
            {(() => {
              const numSelected = this.props.rowStates.filter((rowState) => rowState.selected).length;
              return numSelected > 0 && `${numSelected} selected`;
            })()}
          </Col>
          <Col md={4}>
            <SelectDropdown
              displayName="Set status"
              disabled={this.props.rowStates.every((rowState) => !rowState.selected)}
              menu={this._getStatusMenu()}
            />
          </Col>
        </Row>
        <Row className="u-small-spacing-top">
          <Col md={12}>
            {isLoading ? (
              <CenteredHorizontalThrobber />
            ) : (
              <FulfillmentStatusTable
                hydratedFulfillments={this.props.hydratedFulfillments}
                fulfillmentIdToNewStatus={_.object(
                  this.props.rowStates.map((rowState) => [rowState.id, rowState.newStatus])
                )}
                // rowStateManager
                rowStates={this.props.rowStates.map(({id, selected}) => ({id, selected}))}
                toggleRowSelected={this.props.toggleRowSelected}
                toggleAllRowsSelected={this.props.toggleAllRowsSelected}
              />
            )}
          </Col>
        </Row>
      </Modal>
    );
  }
}

const rowStateManager = manageRowState({
  // @ts-expect-error newStatus should not be nullable
  defaultState: {selected: false, newStatus: null},
  mapPropsToOrderedIds(
    ownProps: BaseUpdateFulfillmentStatusModalProps & AssayStoreProps & ConnectedProps & DispatchProps
  ): Array<string> {
    if (!ownProps.hydratedFulfillments) {
      return [];
    }
    return ownProps.hydratedFulfillments.map((hydratedFulfillment) => hydratedFulfillment.fulfillment.id);
  },
  mapStateToProps(
    rowStates: Array<RowState>,
    updateRowState: (rowId: string, updateFn: (rowState: RowState) => RowState) => void,
    updateEachRowState: (updateFn: (rowState: RowState) => RowState) => void
  ): RowStateManagerProps {
    return {
      rowStates,
      setNewStatusForSelectedRows(newStatus: FulfillmentStatus): void {
        updateEachRowState((rowState) => (rowState.selected ? {...rowState, newStatus} : rowState));
      },
      toggleRowSelected(rowId: string): void {
        updateRowState(rowId, (rowState) => ({...rowState, selected: !rowState.selected}));
      },
      toggleAllRowsSelected(): void {
        const allRowsSelected = rowStates.every((rowState) => rowState.selected);
        updateEachRowState((rowState) => ({...rowState, selected: !allRowsSelected}));
      },
      updateRowState,
      updateEachRowState,
    };
  },
});

function mapDispatchToProps(dispatch: BenchlingDispatch): DispatchProps {
  return {
    async bulkUpdateFulfillments(fulfillmentUpdates: Array<RequestFulfillmentUpdate>): Promise<void> {
      await dispatch(bulkUpdateFulfillments(fulfillmentUpdates));
    },
    fetchRequestFulfillments(fulfillmentIds: Array<string>): void {
      dispatch(fetchRequestFulfillments(fulfillmentIds));
    },
    fetchRequestTaskSchemas(): void {
      dispatch(RequestTaskSchemaReduxActions.fetchAll());
    },
  };
}

export default AssayStore.connect((assayStore: typeof AssayStore) => ({
  requestSchemas: assayStore.getRequestSchemas(),
}))(
  connect(mapStateToProps, mapDispatchToProps)(rowStateManager(UpdateFulfillmentStatusModal))
) as React.ComponentClass<BaseUpdateFulfillmentStatusModalProps>;
